import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import MoneyRain from '../components/MoneyRain';
import MotivationalBanner from '../components/MotivationalBanner';
import { useLanguage } from '../context/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { TrendingUp, Phone, Users, Calendar, DollarSign, Target, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user, onLogout, theme, onToggleTheme }) => {
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchWeekly();
    const interval = setInterval(() => {
      fetchStats();
      fetchWeekly();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchWeekly = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/weekly`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeekly(response.data);
    } catch (error) {
      console.error('Error fetching weekly stats:', error);
    }
  };

  const statCards = [
    {
      title: t('total_leads'),
      value: stats?.total_leads || 0,
      icon: Users,
      color: '#0F62FE',
      testId: 'stat-total-leads',
      route: '/leads',
    },
    {
      title: t('new_leads'),
      value: stats?.new_leads || 0,
      icon: TrendingUp,
      color: '#198038',
      testId: 'stat-new-leads',
      route: '/leads?status=new',
    },
    {
      title: t('calls_today'),
      value: stats?.calls_today || 0,
      icon: Phone,
      color: '#0F62FE',
      testId: 'stat-calls-today',
      route: '/leads?status=contacted',
    },
    {
      title: t('deposits_today'),
      value: stats?.deposits_today || 0,
      icon: DollarSign,
      color: '#198038',
      testId: 'stat-deposits-today',
      route: '/leads?status=converted',
    },
    {
      title: t('appointments_today'),
      value: stats?.appointments_today || 0,
      icon: Calendar,
      color: '#F1C21B',
      testId: 'stat-appointments-today',
      route: '/calendar?view=list&filter=appointments',
    },
    {
      title: t('callbacks_pending'),
      value: stats?.callbacks_pending || 0,
      icon: Clock,
      color: '#FA4D56',
      testId: 'stat-callbacks-pending',
      route: '/calendar?view=list&filter=callbacks',
    },
    {
      title: t('conversion_rate'),
      value: `${stats?.conversion_rate || 0}%`,
      icon: Target,
      color: '#0F62FE',
      testId: 'stat-conversion-rate',
      route: '/leads?status=converted',
    },
    {
      title: t('calls_week'),
      value: stats?.calls_week || 0,
      icon: Phone,
      color: '#525252',
      testId: 'stat-calls-week',
      route: '/leads',
    },
  ];

  const DAY_LABELS_IT = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  const DAY_LABELS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dayLabels = language === 'en' ? DAY_LABELS_EN : DAY_LABELS_IT;
  const chartData = (weekly || []).map((d) => ({
    name: dayLabels[d.day_index] || d.date,
    calls: d.calls,
    deposits: d.deposits,
    appointments: d.appointments,
    callbacks: d.callbacks,
  }));

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        user={user}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div
        className="flex-1 overflow-y-auto relative"
        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
      >
        <MoneyRain count={35} />
        <div className="p-6 lg:p-8 relative" style={{ zIndex: 2 }}>
          <MotivationalBanner theme={theme} userName={user.full_name} />
          <div className="mb-8">
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
              style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
              data-testid="dashboard-title"
            >
              {t('dashboard')}
            </h1>
            <p className="text-base" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
              {t('welcome')}, {user.full_name}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className="stat-card cursor-pointer"
                  data-testid={card.testId}
                  onClick={() => navigate(card.route)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigate(card.route);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-2" style={{ color: card.color }}>
                        {card.title}
                      </p>
                      <p
                        className="text-3xl font-bold font-mono"
                        style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                      >
                        {card.value}
                      </p>
                    </div>
                    <div className="p-2 rounded-md" style={{ backgroundColor: `${card.color}15` }}>
                      <Icon size={24} style={{ color: card.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div
              className="p-6 rounded-lg border"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
              }}
              data-testid="chart-calls-deposits"
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                {t('calls_deposits')}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#262626' : '#E0E0E0'} />
                  <XAxis dataKey="name" stroke={theme === 'dark' ? '#A8A8A8' : '#525252'} />
                  <YAxis stroke={theme === 'dark' ? '#A8A8A8' : '#525252'} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                      border: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}`,
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="calls" fill="#0F62FE" name={language === 'en' ? 'Calls' : 'Chiamate'} />
                  <Bar dataKey="deposits" fill="#198038" name={language === 'en' ? 'Deposits' : 'Depositi'} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div
              className="p-6 rounded-lg border"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
              }}
              data-testid="chart-performance"
            >
              <h3 className="text-xl font-bold mb-4" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                {t('weekly_performance')}
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#262626' : '#E0E0E0'} />
                  <XAxis dataKey="name" stroke={theme === 'dark' ? '#A8A8A8' : '#525252'} />
                  <YAxis stroke={theme === 'dark' ? '#A8A8A8' : '#525252'} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                      border: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}`,
                      borderRadius: '8px',
                    }}
                  />
                  <Line type="monotone" dataKey="calls" stroke="#0F62FE" strokeWidth={2} name={language === 'en' ? 'Calls' : 'Chiamate'} />
                  <Line type="monotone" dataKey="deposits" stroke="#198038" strokeWidth={2} name={language === 'en' ? 'Deposits' : 'Depositi'} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
